import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { usePushNotifications } from './usePushNotifications';
import { REMOVE_USERS_FID, UPDATE_USER_ALLOW_NOTIFICATIONS, UPDATE_USERS_FID } from './api/PushNotificationQuery';
import { clearSavedFid, getSavedFid, getSavedPermission, setSavedFid, setSavedPermission } from './pushRegistrationStore';
import { FETCH_POLICY_NO_CACHE } from '../utils/constants';

interface UseStudentPushNotificationsOptions {
  /** Register and save this browser's FID (a student is logged in) */
  enabled: boolean;
  /** Show notifications that arrive while Classroom is open */
  displayEnabled: boolean;
  /** Logged-in user's uId (needed to save the notification permission) */
  userId?: string | null;
}

/**
 * Web push for students, connected to the backend:
 * - the browser's FID is saved with updateUsersFid (replacing this browser's previous FID), only when it changes
 * - the user's answer to the notification prompt is saved as uAllowNotifications, only when it changes
 */
export const useStudentPushNotifications = ({ enabled, displayEnabled, userId }: UseStudentPushNotificationsOptions) => {
  const [updateUsersFid] = useMutation(UPDATE_USERS_FID, { fetchPolicy: FETCH_POLICY_NO_CACHE });
  const [removeUsersFid] = useMutation(REMOVE_USERS_FID, { fetchPolicy: FETCH_POLICY_NO_CACHE });
  const [updateAllowNotifications] = useMutation(UPDATE_USER_ALLOW_NOTIFICATIONS, { fetchPolicy: FETCH_POLICY_NO_CACHE });

  const handleRegistered = useCallback(
    (fid: string) => {
      if (!userId) return;
      const saved = getSavedFid();
      if (saved?.userId === userId && saved.fid === fid) return;
      // Only this user's own previous FID is replaced; without one, oldFid isn't sent at all
      const oldFid = saved?.userId === userId ? saved.fid : null;
      updateUsersFid({ variables: oldFid ? { fid, oldFid } : { fid } })
        .then(() => setSavedFid({ userId, fid }))
        .catch(() => undefined);
    },
    [userId, updateUsersFid],
  );

  // Firebase dropped this browser's registration while the student is logged in: remove it from the backend too,
  // and forget it locally so the next registration is saved again. (After logout the saved FID is already gone.)
  const handleUnregistered = useCallback(
    (fid: string) => {
      if (getSavedFid()?.fid !== fid) return;
      clearSavedFid();
      removeUsersFid({ variables: { fid } }).catch(() => undefined);
    },
    [removeUsersFid],
  );

  const handlePermission = useCallback(
    (allowed: boolean) => {
      if (!userId) return;
      const saved = getSavedPermission();
      if (saved?.userId === userId && saved.allowed === allowed) return;
      updateAllowNotifications({ variables: { uId: userId, allowNotifications: allowed } })
        .then(() => setSavedPermission({ userId, allowed }))
        .catch(() => undefined);
    },
    [userId, updateAllowNotifications],
  );

  // Any logout, including automatic ones (session expired, access revoked), ends the logged-in state. Unregister this
  // browser from FCM then, so no notifications arrive after logout. This is a Firebase call, so it works even when the
  // session is already gone and the backend can't be called. (A manual logout has already done this; it's harmless twice.)
  const wasLoggedInRef = useRef(enabled);
  useEffect(() => {
    if (wasLoggedInRef.current && !enabled) {
      clearSavedFid();
      import('./messaging')
        .then(({ stopPushRegistration }) => stopPushRegistration())
        .catch(() => undefined);
    }
    wasLoggedInRef.current = enabled;
  }, [enabled]);

  usePushNotifications({
    // Waits for the user id, so the FID and permission are always saved for the right user
    enabled: enabled && Boolean(userId),
    displayEnabled,
    onRegistered: handleRegistered,
    onUnregistered: handleUnregistered,
    onPermission: handlePermission,
  });
};
